'use client';

import { useEffect } from 'react';

export interface SessionTimeoutModalProps {
  isOpen: boolean;
  secondsUntilTimeout: number;
  onExtend: () => void;
  onLogout: () => void;
  requiresReauthentication?: boolean;
}

export function SessionTimeoutModal({ isOpen, secondsUntilTimeout, onExtend, onLogout, requiresReauthentication = false }: SessionTimeoutModalProps) {
  const minutes = Math.floor(secondsUntilTimeout / 60);
  const seconds = secondsUntilTimeout % 60;

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onExtend();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onExtend]);

  if (!isOpen) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-timeout-title"
      aria-describedby="session-timeout-description"
      data-tour="session-timeout-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/55 px-4 py-4"
    >
      <div className="w-full max-w-md rounded-[var(--k-radius-lg)] border border-[var(--k-color-warning)]/40 bg-[var(--k-color-surface)] p-6 shadow-[var(--k-shadow-lg)]">
        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-[var(--k-color-warning-soft)] text-[var(--k-color-warning-strong)]">
          <span aria-hidden="true" className="text-lg font-bold">⏱</span>
        </div>
        <h3 id="session-timeout-title" className="text-lg font-semibold text-[var(--k-color-text-primary)]">
          Deine Session läuft ab
        </h3>
        <p id="session-timeout-description" className="mt-2 text-sm text-[var(--k-color-text-secondary)]">
          Du wirst in{' '}
          <span className="font-mono font-bold text-[var(--k-color-warning-strong)]" data-tour="session-timeout-countdown">
            {minutes}:{String(seconds).padStart(2, '0')}
          </span>
          {' '}automatisch abgemeldet. {requiresReauthentication ? 'Melde dich erneut an, um weiterzuarbeiten.' : 'Möchtest du angemeldet bleiben?'}
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={onLogout}
            data-tour="session-timeout-logout"
            className="rounded-[var(--k-radius-md)] border border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] px-4 py-2 text-sm font-semibold text-[var(--k-color-text-secondary)] transition hover:border-[var(--k-color-border-strong)] hover:text-[var(--k-color-text-primary)]"
          >
            Jetzt abmelden
          </button>
          <button
            type="button"
            onClick={onExtend}
            data-tour="session-timeout-extend"
            autoFocus
            className="rounded-[var(--k-radius-md)] bg-[var(--k-color-accent)] px-4 py-2 text-sm font-bold text-white shadow-[var(--k-shadow-sm)] transition hover:brightness-110"
          >
            {requiresReauthentication ? 'Erneut anmelden' : 'Angemeldet bleiben'}
          </button>
        </div>
      </div>
    </div>
  );
}
